import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import { createAuthSlice } from './slices/authSlice';
import type { AuthSlice } from './slices/authSlice';
import { createNotificationSlice } from './slices/notificationSlice';
import type { NotificationSlice } from './slices/notificationSlice';
import { createSettingsSlice } from './slices/settingsSlice';
import type { SettingsSlice } from './slices/settingsSlice';
import { createWhatsNewSlice } from './slices/whatsNewSlice';
import type { WhatsNewSlice } from './slices/whatsNewSlice';
import { createPlanningYearSlice } from './slices/planningYearSlice';
import type { PlanningYearSlice } from './slices/planningYearSlice';

/**
 * Объединённый тип для всего App Store
 */
export type AppStore = AuthSlice & NotificationSlice & SettingsSlice & WhatsNewSlice & PlanningYearSlice;

/**
 * Старый ключ localStorage для обратной совместимости
 */
const OLD_STORAGE_KEY = 'plng_notifications';
const NEW_STORAGE_KEY = 'plng-app-store';

/**
 * Тип данных старого store (для миграции)
 */
interface OldNotificationStore {
  state?: {
    notifications?: unknown[];
    settings?: {
      disabledTypes?: string[];
      showInPopover?: boolean;
    };
  };
}

/**
 * Тип частичного состояния для миграции (соответствует partialize)
 */
type PartializedState = {
  notifications: unknown[];
  settings: {
    disabledTypes: string[];
    showInPopover: boolean;
  };
  displayMode: string;
  themeMode: string;
};

/**
 * Мигрирует данные из старого ключа localStorage в новый
 * Выполняется один раз при загрузке модуля, до создания store
 * Сохраняет данные в формате, который ожидает Zustand persist middleware
 */
function migrateOldNotificationData(): void {
  let storage: Storage | undefined;
  try {
    const candidate = globalThis.localStorage;
    if (
      !candidate
      || typeof candidate.getItem !== 'function'
      || typeof candidate.setItem !== 'function'
      || typeof candidate.removeItem !== 'function'
    ) {
      return;
    }
    storage = candidate;

    // Проверяем, есть ли уже новые данные
    const newData = storage.getItem(NEW_STORAGE_KEY);
    if (newData) {
      // Миграция уже выполнена или данные уже в новом формате
      return;
    }

    // Проверяем наличие старых данных
    const oldDataRaw = storage.getItem(OLD_STORAGE_KEY);
    if (!oldDataRaw) {
      // Старых данных нет, миграция не требуется
      return;
    }

    // Парсим старые данные
    let oldData: OldNotificationStore;
    try {
      oldData = JSON.parse(oldDataRaw);
    } catch (error) {
      if (import.meta.env.DEV) {
        console.warn('⚠️ Не удалось распарсить старые данные уведомлений:', error);
      }
      // Удаляем поврежденные данные
      storage.removeItem(OLD_STORAGE_KEY);
      return;
    }

    // Извлекаем данные из старого формата
    const notifications = oldData.state?.notifications || [];
    const settings = oldData.state?.settings || {
      disabledTypes: [],
      showInPopover: true,
    };

    // Валидируем и нормализуем данные
    const partializedState: PartializedState = {
      notifications: Array.isArray(notifications) ? notifications : [],
      settings: {
        disabledTypes: Array.isArray(settings.disabledTypes) ? settings.disabledTypes : [],
        showInPopover: settings.showInPopover !== undefined ? settings.showInPopover : true,
      },
      displayMode: 'stack',
      themeMode: 'light',
    };

    // Сохраняем в формате, который ожидает Zustand persist
    // Формат: { state: {...partialized state...}, version: 0 }
    const newDataFormat = {
      state: partializedState,
      version: 0,
    };

    // Сохраняем в новый ключ
    storage.setItem(NEW_STORAGE_KEY, JSON.stringify(newDataFormat));

    // Удаляем старый ключ после успешной миграции
    storage.removeItem(OLD_STORAGE_KEY);

    if (import.meta.env.DEV) {
      console.log('✅ Миграция данных уведомлений выполнена:', {
        notificationsCount: partializedState.notifications.length,
        settings: partializedState.settings,
      });
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('❌ Ошибка при миграции данных уведомлений:', error);
    }
    // В случае ошибки не блокируем работу приложения
    // Просто удаляем старые данные, чтобы не пытаться мигрировать их снова
    try {
      storage?.removeItem(OLD_STORAGE_KEY);
    } catch {
      // Игнорируем ошибки удаления
    }
  }
}

// Выполняем миграцию при загрузке модуля, до создания store
// Это гарантирует, что Zustand найдет данные в новом ключе при первой загрузке
if (typeof window !== 'undefined') {
  migrateOldNotificationData();
}

/**
 * Главный store приложения с использованием Slices Pattern
 * 
 * Объединяет:
 * - AuthSlice - управление токеном авторизации
 * - NotificationSlice - управление уведомлениями
 * - SettingsSlice - пользовательские настройки (displayMode)
 * 
 * Middleware:
 * - devtools - интеграция с Redux DevTools
 * - persist - сохранение состояния в localStorage
 * 
 * @example
 * ```tsx
 * // Использование auth slice
 * const setToken = useAppStore(state => state.setAccessTokenProvider);
 * 
 * // Использование notification slice
 * const addNotification = useAppStore(state => state.addNotification);
 * const notifications = useAppStore(state => state.notifications);
 * ```
 */
export const useAppStore = create<AppStore>()(
  devtools(
    persist(
      (...a) => ({
        ...createAuthSlice(...a),
        ...createNotificationSlice(...a),
        ...createSettingsSlice(...a),
        ...createWhatsNewSlice(...a),
        ...createPlanningYearSlice(...a),
      }),
      {
        name: NEW_STORAGE_KEY,
        partialize: (state) => ({
          notifications: state.notifications,
          settings: state.settings,
          displayMode: state.displayMode,
          themeMode: state.themeMode,
          lastSeenVersion: state.lastSeenVersion,
          planningYear: state.planningYear,
        }),
      }
    ),
    { 
      name: 'PLNG Store',
      enabled: import.meta.env.DEV, // DevTools только в development
    }
  )
);

